import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useQuery } from 'react-query';
import RxLabel, { RX_LABEL_SIZES, RxLabelSampleData } from '../../components/Medication/RxLabel';
import { printRxLabels } from '../../components/Medication/printLabel';
import api from '../../utils/api';

/**
 * PrintersSettings — print a test label so the user can confirm their label
 * printer is configured before they start dispensing real Rxs. Works with any
 * label printer the OS knows about, network or USB; the OS print dialog
 * handles printer selection and the test label is sized for a standard
 * ~89 × 28 mm Rx bottle label.
 */

const LABEL_SIZE = 'DYMO_30252' as const;

const SAMPLE_DATA: RxLabelSampleData = {
  clinic: {
    name: 'StableTrack Equine Veterinary',
    address: '123 Equine Way, Lexington KY 40502',
    phone: '(555) 555-0100',
  },
  vet: {
    name: 'Dr. Sarah Mitchell, DVM',
    license: 'KY-VET-12345',
    deaNumber: 'BM1234567',
  },
  patient: {
    name: 'Thunder',
    species: 'Equine',
    owner: 'James Thornton',
  },
  rx: {
    drug: 'Flunixin Meglumine 50 mg/mL (Banamine)',
    quantity: '100 mL',
    sig: 'Give 1.1 mg/kg IV BID for 3 days for pain and inflammation.',
    issued: '2026-05-12',
    expires: '2027-05-12',
    refillsRemaining: 2,
    refillsAuthorized: 4,
  },
  warnings: ['Keep refrigerated. Protect from light.'],
};

const PrintersSettings: React.FC = () => {
  const cfg = RX_LABEL_SIZES[LABEL_SIZE];

  // Pull the clinic's real header (name/address/phone/logo) so the test label
  // previews exactly what a dispensed label will look like — including the
  // uploaded logo. Falls back to the generic sample if the clinic can't be
  // loaded (e.g. offline); a test print is not a clinical action.
  const { data: clinic } = useQuery(
    ['clinic', 'current', 'label-test'],
    () => api.get('/clinics/current').then((r) => r.data),
    { staleTime: 5 * 60 * 1000, retry: false },
  );

  const sampleData: RxLabelSampleData = clinic
    ? {
        ...SAMPLE_DATA,
        clinic: {
          name: clinic.name || SAMPLE_DATA.clinic.name,
          address: clinic.address || SAMPLE_DATA.clinic.address,
          phone: clinic.phone || SAMPLE_DATA.clinic.phone,
          logoUrl: clinic.logoUrl || undefined,
        },
      }
    : SAMPLE_DATA;

  const handlePrintTest = () => {
    void printRxLabels({ size: LABEL_SIZE, labels: [{ data: sampleData }] });
  };

  return (
    <Box sx={{ maxWidth: 'sm' }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Label Printer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Print a test label to confirm your label printer is working. Works
        with any label printer your computer can see — pick the printer (and
        any network printer it knows about) in the print dialog. The test
        label is sized for {cfg.widthMm} × {cfg.heightMm}&nbsp;mm Rx stock.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 3,
          py: 3,
          px: 2,
          bgcolor: 'grey.100',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'grey.300',
        }}
      >
        <RxLabel size={LABEL_SIZE} sample={sampleData} />
      </Box>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrintTest}
        >
          Print test label
        </Button>
      </Stack>
    </Box>
  );
};

export default PrintersSettings;
